/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * 데이터스토어 공통 클래스를 정의한다.
 *
 * @file /src/models/store/Store.ts
 * @author sungjin <esung246@naddle.net>
 * @license MIT License
 * @modified 2026. 9. 18.
 */
import * as parent from '@base/Base';
import { Record } from '@models/store/Record';
import { Group } from '@models/store/Group';
import { Provider } from '@models/store/Provider';
import { Remote as RemoteProvider } from '@models/store/provider/Remote';
import { Local as ListLocal } from '@models/store/list/Local';
import { Remote as ListRemote } from '@models/store/list/Remote';
import { Local as TreeLocal } from '@models/store/tree/Local';
import { Remote as TreeRemote } from '@models/store/tree/Remote';
import { Format } from '@utils/Format';
import { Chunks } from '@utils/Chunks';
import { Exceptions } from '@utils/Exceptions';

export type ListStores = ListLocal | ListRemote;
export type TreeStores = TreeLocal | TreeRemote;
export type Stores = ListLocal | ListRemote | TreeLocal | TreeRemote;

export interface Listeners<T> extends parent.Listeners<T> {
    /**
     * 데이터를 로딩하기 전
     */
    beforeLoad?: (me: T) => void | Promise<void>;

    /**
     * 데이터가 로딩되었을 때
     */
    load?: (me: T) => void | Promise<void>;

    /**
     * 데이터가 변경되었을 때
     */
    update?: (me: T) => void | Promise<void>;

    /**
     * 레코드값이 변경되었을 때
     */
    edit?: (record: Record) => void | Promise<void>;
}

export interface Properties<T, P extends Provider> extends parent.Properties<T> {
    /**
     * 데이터 프로바이더 설정
     */
    provider: P['properties'];

    /**
     * 레코드 고유값 필드명
     */
    primaryKeys?: string[];

    /**
     * 필드정의
     */
    fields?: (string | { name: string; type: 'int' | 'float' | 'string' | 'boolean' | 'object' | 'date' })[];

    /**
     * 데이터 정렬방식
     */
    sorters?: { [field: string]: 'ASC' | 'DESC' | any[] };

    /**
     * 데이터 필터
     */
    filters?: { [field: string]: { value: any; operator?: string } | string };

    /**
     * 필터모드
     */
    filterMode?: 'OR' | 'AND';

    /**
     * 이벤트리스너
     */
    listeners?: Listeners<T>;
}

export abstract class Store<P extends Provider, R extends number | number[]> extends parent.Base {
    /**
     * 스토어타입
     */
    abstract type: R extends number ? 'list' : 'tree';

    /**
     * 데이터 프로바이더
     */
    provider: P;

    /**
     * 레코드 고유값 필드명
     */
    primaryKeys: string[] = null;

    /**
     * 필드정의
     */
    fields: {
        [name: string]: {
            type: 'int' | 'float' | 'string' | 'boolean' | 'object' | 'date' | 'children';
            is_primary: boolean;
        };
    } = {};

    /**
     * 데이터 그룹방식
     */
    grouper: { field: string; sorters: { [field: string]: 'ASC' | 'DESC' | any[] } } = null;

    /**
     * 데이터 정렬방식
     */
    sorters: { [field: string]: 'ASC' | 'DESC' | any[] } = null;

    /**
     * 데이터 필터
     */
    filters: { [field: string]: { value: any; operator: string } } = null;

    /**
     * 필터모드
     */
    filterMode: 'OR' | 'AND' = 'AND';

    /**
     * 모든 레코드셋
     */
    records: Map<string, Record> = new Map();

    /**
     * 최상위 원본 레코드 아이디
     */
    rootIds: string[] = [];

    /**
     * 레코드 아이디(정렬 및 필터링된 아이디)
     */
    recordIds: string[] = [];

    /**
     * 수정된 레코드셋
     */
    updatedRecords: Map<string, Record> = new Map();

    /**
     * 삭제된 레코드셋
     */
    deletedRecords: Map<string, Record> = new Map();

    /**
     * 모든 그룹정보
     */
    groups: Map<any, Group> = new Map();

    /**
     * 그룹 아이디(정렬된 그룹아이디)
     */
    groupIds: any[] = [];

    /**
     * 현재 데이터 그룹방식
     */
    currentGrouper: { field: string; sorters: { [field: string]: 'ASC' | 'DESC' | any[] } };

    /**
     * 현재 데이터 정렬방식
     */
    currentSorters: { [field: string]: 'ASC' | 'DESC' | any[] };

    /**
     * 현재 데이터 필터
     */
    currentFilters: { [field: string]: { value: any; operator: string } };

    /**
     * 현재 데이터 필터 모드
     */
    currentFilterMode: 'OR' | 'AND';

    /**
     * 현재 진행중인 로드함수
     */
    #loading: Promise<void> | null = null;

    /**
     * 로딩이 진행되는 동안 release() 에 의해 참조가 모두 해제되었는지 여부
     */
    #pendingRelease: boolean = false;

    /**
     * 데이터스토어를 정의한다.
     *
     * @param properties - 초기속성
     */
    constructor(public properties: Properties<Store<P, R>, P> = null) {
        super(properties);

        this.primaryKeys = this.properties.primaryKeys ?? null;
        for (let field of this.properties.fields ?? []) {
            if (typeof field == 'string') {
                this.fields[field] = { type: 'string', is_primary: false };
            } else {
                this.fields[field.name] = { type: field.type, is_primary: false };
            }
        }

        if (this.primaryKeys !== null) {
            for (const key of this.primaryKeys) {
                this.fields[key] = { type: this.fields[key]?.type ?? 'string', is_primary: true };
            }
        }

        this.sorters = this.properties.sorters ?? null;
        for (const name in this.properties.filters ?? {}) {
            let filter: { value: any; operator: string };
            if (typeof this.properties.filters[name] == 'string') {
                filter = { value: this.properties.filters[name], operator: '=' };
            } else {
                filter = {
                    value: this.properties.filters[name].value,
                    operator: this.properties.filters[name].operator ?? '=',
                };
            }
            this.filters ??= {};
            this.filters[name] = filter;
        }
        this.filterMode = this.properties.filterMode?.toUpperCase() == 'OR' ? 'OR' : 'AND';
    }

    /**
     * 레코드를 초기화한다.
     *
     * @param records - 레코드목록
     */
    async init(records: { [key: string]: any }[]): Promise<void> {
        this.rootIds = [];
        if (records.length > 0) {
            const chunks = new Chunks(records);
            await chunks.execute((chunk) => {
                this.set(chunk, true);
            });
        }
    }

    /**
     * 레코드 고유값 필드를 설정한다.
     * 데이터스토어가 로드되기전에만 적용된다.
     *
     * @param primaryKeys - 레코드 고유값 필드
     * @param is_replace - 기존 정의된 레코드 고유값 필드를 재정의할지 여부 (FALSE 인 경우 기존에 이미 정의된 경우 재정의 하지 않는다.)
     */
    setPrimaryKeys(primaryKeys: string[] = null, is_replace: boolean = true): void {
        if (this.isLoaded() == true) {
            Exceptions.warn('[Store]', 'ALREADY_DATA_LOADED');
            return;
        }

        if (this.primaryKeys === null || is_replace === true) {
            this.primaryKeys = primaryKeys;
            for (const key in this.fields) {
                if (primaryKeys !== null && primaryKeys.includes(key) == true) {
                    this.fields[key].is_primary = true;
                } else {
                    this.fields[key].is_primary = false;
                }
            }

            if (primaryKeys !== null) {
                for (const key of primaryKeys) {
                    this.fields[key] = { type: this.fields[key]?.type ?? 'string', is_primary: true };
                }
            }
        }
    }

    /**
     * 데이터 프로바이더를 가져온다.
     *
     * @returns 데이터 프로바이더
     */
    getProvider(): P {
        return this.provider as P;
    }

    /**
     * 현재 활성화된 레코드셋을 가져온다.
     *
     * @returns 활성화된 레코드셋
     */
    getRecords(is_origin_records: boolean = false): Record[] {
        if (is_origin_records == true) {
            return this.rootIds.map((id: string) => this.records.get(id));
        } else {
            return this.recordIds.map((id: string) => this.records.get(id));
        }
    }

    /**
     * 현재 활성화된 레코드고유값 목록을 가져온다.
     *
     * @returns 활성화된 레코드고유값 목록
     */
    getRecordIds(): string[] {
        return this.recordIds;
    }

    /**
     * 특정 레코드아이디가 존재하는지 확인한다.
     *
     * @returns 레코드 존재여부
     */
    hasRecordId(record_id: string): boolean {
        return this.recordIds.indexOf(record_id) > -1;
    }

    /**
     * 데이터 그룹을 가져온다.
     *
     * @param groupId 그룹고유값
     * @returns 데이터 그룹
     */
    getGroup(groupId: any): Group {
        return this.groups.get(groupId);
    }

    /**
     * 데이터 그룹고유값 목록을 가져온다.
     *
     * @returns 데이터 그룹고유값 목록
     */
    getGroupIds(): any[] {
        return this.groupIds;
    }

    /**
     * 그룹 필드를 가져온다.
     *
     * @returns 그룹 필드
     */
    getGroupField(): string | null {
        return this.grouper?.field ?? null;
    }

    /**
     * 그룹 정렬을 가져온다.
     *
     * @returns 그룹 정렬
     */
    getGroupSorter(): { [field: string]: 'ASC' | 'DESC' | any[] } | null {
        return this.grouper?.sorters ?? null;
    }

    /**
     * 그룹 내 레코드고유값 목록을 가져온다.
     *
     * @param groupId - 그룹고유값
     * @returns 그룹 내 레코드고유값 목록
     */
    getRecordIdsByGroup(groupId: string): string[] {
        return this.groups.get(groupId).getRecordIds();
    }

    /**
     * 레코드고유값으로 레코드를 가져온다.
     *
     * @param id - 레코드아이디
     * @returns 레코드
     */
    get(id: string): Record | null {
        return this.records.get(id) ?? null;
    }

    /**
     * 레코드데이터를 레코드로 지정한다.
     * 이미 스토어 내에 동일 레코드가 존재한다면 NULL 을 반환한다.
     *
     * @param data - 레코드데이터
     * @returns 레코드
     */
    set(data: { [field: string]: any }, is_root: boolean = true): Record | null {
        let record = new Record(data, this as any);
        if (this.records.has(record.getId()) == true) {
            record = this.records.get(record.getId());
        } else {
            this.records.set(record.getId(), record);

            if (is_root == true) {
                this.rootIds.push(record.getId());
            }
        }

        return record;
    }

    /**
     * 특정 레코드아이디를 레코드에서 삭제한다.
     *
     * @param record_id - 레코드아이디
     */
    unset(record_id: string): void {
        let origin: Record = null;
        if (this.records.has(record_id) == true) {
            origin = this.records.get(record_id);
            this.records.get(record_id).destroy();
            this.records.delete(record_id);
        }

        this.rootIds = this.rootIds.filter((id) => id !== record_id);
        this.recordIds = this.recordIds.filter((id) => id !== record_id);
        this.deletedRecords.set(record_id, origin);
    }

    /**
     * 불러온 레코드 갯수를 가져온다.
     *
     * @returns 불러온 레코드 갯수
     */
    getCount(): number {
        return this.recordIds.length;
    }

    /**
     * 변경된 레코드를 가져온다.
     *
     * @param record_id - 레코드고유값
     * @returns 변경된 레코드
     */
    getUpdated(record_id: string): Record | null {
        return this.updatedRecords.get(record_id) ?? null;
    }

    /**
     * 삭제된 레코드를 가져온다.
     *
     * @param record_id - 레코드고유값
     * @returns 삭제된 레코드
     */
    getDeleted(record_id: string): Record | null {
        return this.deletedRecords.get(record_id) ?? null;
    }

    /**
     * 데이터 그룹방식을 변경한다.
     *
     * @param grouper - 데이터 그룹방식
     */
    async setGroup(
        grouper: { field: string; sorters: { [field: string]: 'ASC' | 'DESC' | any[] } } | null
    ): Promise<void> {
        if (Format.isEqual(this.currentGrouper, grouper) == true) {
            return;
        }

        this.grouper = grouper;

        if (this.provider instanceof RemoteProvider && this.provider.remoteSort == true) {
            if (this.isLoaded() == true) {
                await this.reload();
            }
        } else {
            await this.#group();
        }
    }

    /**
     * 데이터를 정렬한다.
     *
     * @param field - 정렬할 데이터인덱스
     * @param direction - 정렬방향
     */
    async sort(field: string, direction: 'ASC' | 'DESC' | any[]): Promise<void> {
        let sorters: { [field: string]: 'ASC' | 'DESC' | any[] } = {};
        sorters[field] = direction;
        await this.setSorters(sorters);
    }

    /**
     * 데이터를 정렬방식을 변경한다.
     *
     * @param sorters - 정렬방식
     */
    async setSorters(sorters: { [field: string]: 'ASC' | 'DESC' | any[] }): Promise<void> {
        if (Format.isEqual(this.currentSorters, sorters, true) == true) {
            return;
        }

        this.sorters = sorters === null ? null : { ...sorters };
        if (this.sorters === null) {
            this.currentSorters = null;
            return;
        }

        if (this.provider instanceof RemoteProvider && this.provider.remoteSort == true) {
            if (this.isLoaded() == true) {
                await this.reload();
                this.currentSorters = this.sorters === null ? null : { ...this.sorters };
            }
        } else {
            await this.#sort();
        }
    }

    /**
     * 레코드 위치를 스왑한다.
     *
     * @param left - 왼쪽 레코드고유값
     * @param right - 오른쪽 레코드고유값
     * @param is_fire_event - 이벤트 발생여부
     */
    async swap(left: string, right: string, is_fire_event: boolean = true): Promise<void> {
        const leftRecord = this.records.get(left);
        const rightRecord = this.records.get(right);
        if (leftRecord === undefined || rightRecord === undefined) {
            return;
        }

        /**
         * 부모가 다른 레코드(형제관계가 아님)간의 스왑은 허용하지 않는다.
         */
        const leftParent = leftRecord.getParent();
        const rightParent = rightRecord.getParent();

        if (leftParent !== rightParent) {
            return;
        }

        if (leftParent === null) {
            const leftIndex = this.recordIds.indexOf(left);
            const rightIndex = this.recordIds.indexOf(right);
            if (leftIndex === -1 || rightIndex === -1) {
                return;
            }

            this.recordIds[leftIndex] = right;
            this.recordIds[rightIndex] = left;

            const groupId = this.grouper?.field ? leftRecord.get(this.grouper.field) : 'none';
            const group = this.groups.get(groupId);
            if (group !== undefined) {
                const groupLeftIndex = group.recordIds.indexOf(left);
                const groupRightIndex = group.recordIds.indexOf(right);
                if (groupLeftIndex !== -1 && groupRightIndex !== -1) {
                    group.recordIds[groupLeftIndex] = right;
                    group.recordIds[groupRightIndex] = left;
                }
            }
        } else {
            const childIds = leftParent.childIds;
            const leftIndex = childIds?.indexOf(left) ?? -1;
            const rightIndex = childIds?.indexOf(right) ?? -1;

            if (leftIndex === -1 || rightIndex === -1) {
                return;
            }

            childIds[leftIndex] = right;
            childIds[rightIndex] = left;
        }

        if (is_fire_event == true) {
            await this.fireEvent('update', [this]);
        }
    }

    /**
     * 레코드의 특정필드의 값을 레코드정렬인덱스(0부터 시작)으로 정리한다.
     *
     * @param field - 정렬순서를 입력할 레코드필드명
     * @param is_fire_event - 이벤트 발생여부
     */
    async reindex(field: string, is_fire_event: boolean = true): Promise<void> {
        for (const group_id of this.getGroupIds()) {
            const group = this.getGroup(group_id);
            for (let i = 0; i < group.getRecordIds().length; i++) {
                const record = this.records.get(group.getRecordIds()[i]);
                if (record !== undefined) {
                    await record.set(field, i, false);
                    await record.reindex(field);
                }
            }
        }

        if (is_fire_event == true) {
            await this.fireEvent('update', [this]);
        }
    }

    /**
     * 현재 정렬방식을 가져온다.
     *
     * @returns 정렬방식
     */
    getSorters(): { [field: string]: 'ASC' | 'DESC' | any[] } {
        return this.sorters ?? {};
    }

    /**
     * 특정 필드의 현재 정렬방식을 가져온다.
     *
     * @param 정렬 조회를 위한 데이터인덱스
     * @returns 정렬방식
     */
    getSorter(field: string): 'ASC' | 'DESC' | null {
        if (this.sorters === null) {
            return null;
        }
        const direction = this.sorters[field] ?? null;
        if (Array.isArray(direction) == true) {
            return 'ASC';
        }

        return direction;
    }

    /**
     * 데이터를 필터링한다.
     *
     * @param field - 필터를 적용할 데이터인덱스
     * @param value - 필터값
     * @param operator - 필터조건
     */
    async filter(field: string, value: any, operator: string = '='): Promise<void> {
        let filters: { [field: string]: { value: any; operator: string } } = {};
        filters[field] = { value: value, operator: operator };
        await this.setFilters(filters);
    }

    /**
     * 데이터 필터방식을 변경한다.
     *
     * @param filters - 필터방식
     * @param filterMode - 필터모드
     */
    async setFilters(
        filters: { [field: string]: { value: any; operator: string } | string } | null,
        filterMode: 'OR' | 'AND' = 'AND'
    ): Promise<void> {
        if (Format.isEqual(this.currentFilters, filters) == true && this.currentFilterMode == filterMode) {
            return;
        }

        this.filters = null;
        if (filters !== null) {
            for (const name in filters ?? {}) {
                let filter: { value: any; operator: string };
                if (typeof filters[name] == 'string') {
                    filter = { value: filters[name], operator: '=' };
                } else {
                    filter = {
                        value: filters[name].value,
                        operator: filters[name].operator ?? '=',
                    };
                }
                this.filters ??= {};
                this.filters[name] = filter;
            }
        }

        this.filterMode = filterMode;

        if (this.provider instanceof RemoteProvider && this.provider.remoteFilter == true) {
            if (this.isLoaded() == true) {
                await this.reload();
                this.currentFilters = this.filters;
                this.currentFilterMode = this.filterMode;
            }
        } else {
            await this.#filter();
        }
    }

    /**
     * 현재 필터방식을 가져온다.
     *
     * @returns 필터방식
     */
    getFilters(): { [field: string]: { value: any; operator: string } } {
        return this.filters ?? {};
    }

    /**
     * 현재 필터모드를 가져온다.
     *
     * @returns 필터모드
     */
    getFilterMode(): 'OR' | 'AND' {
        return this.filterMode ?? 'AND';
    }

    /**
     * 특정 필드의 현재 필터방식을 가져온다.
     *
     * @param 필터 조회를 위한 데이터인덱스
     * @returns 필터방식
     */
    getFilter(field: string): { value: any; operator: string } | null {
        if (this.filters === null) {
            return null;
        }
        const filter = this.filters[field] ?? null;
        return filter;
    }

    /**
     * 모든 데이터를 삭제한다.
     */
    async empty(is_fire_event: boolean = true): Promise<void> {
        await this.reset();

        if (is_fire_event == true) {
            await this.onLoad();
        }
    }

    /**
     * 특정 필드의 특정값을 가진 레코드를 찾는다.
     *
     * @param target - 검색 대상
     * @returns 검색된 레코드
     */
    async find(target: { [key: string]: any }): Promise<Record | null> {
        for (const record of this.records.values()) {
            for (const field in target) {
                if (Format.isEqual(record.get(field), target[field]) == true) {
                    return record;
                }
            }
        }

        return null;
    }

    /**
     * 데이터와 일치하는 레코드를 찾는다.
     *
     * @param target - 검색 대상
     * @returns 검색된 레코드
     */
    async match(target: Record | { [key: string]: any }): Promise<Record | null> {
        for (const record of this.records.values()) {
            if (record.isEqual(target) == true) {
                return record;
            }
        }

        return null;
    }

    /**
     * 변경된 데이터를 커밋한다.
     *
     * @param is_all - 전체 데이터를 커밋할지 여부 (false 인 경우 변경된 데이터만 커밋)
     * @returns 변경 완료 여부
     */
    async commit(is_all: boolean = false): Promise<boolean> {
        const success = await this.getProvider().commit(is_all);
        if (success == true) {
            this.updatedRecords.clear();
            this.deletedRecords.clear();
        }

        return success;
    }

    /**
     * 데이터를 추가한다.
     *
     * @param records - 추가할 레코드
     * @param is_fire_event - 이벤트 발생여부
     */
    async add(
        records: { [key: string]: any } | { [key: string]: any }[],
        is_fire_event: boolean = true
    ): Promise<void> {
        if (this.isLoaded() == false) {
            await this.load(is_fire_event);
        }

        if (Array.isArray(records) == true) {
            for (const record of records) {
                this.set(record, true);
            }
        } else {
            this.set(records, true);
        }

        await this.#filter(false);
        await this.#group(is_fire_event);
    }

    /**
     * 데이터를 삭제한다.
     *
     * @param record_id - 삭제할 레코드고유값
     * @param is_fire_event - 이벤트 발생여부
     */
    async delete(record_id: string | string[] | Record | Record[], is_fire_event: boolean = true): Promise<void> {
        if (this.isLoaded() == false) {
            await this.load();
        }

        if (Array.isArray(record_id) == true) {
            for (const record of record_id) {
                if (record instanceof Record) {
                    this.unset(record.getId());
                } else {
                    this.unset(record);
                }
            }
        } else {
            if (record_id instanceof Record) {
                this.unset(record_id.getId());
            } else {
                this.unset(record_id);
            }
        }

        await this.#group(is_fire_event);
    }

    /**
     * 현재 데이터 그룹/정렬/필터 방식을 초기화한다.
     */
    async reset(): Promise<void> {
        if (this.#loading !== null) {
            await this.#loading;
        }

        await this.provider.reset();

        this.recordIds = [];
        this.records.clear();
        this.groupIds = [];
        this.groups.clear();
        this.currentSorters = undefined;
        this.currentGrouper = undefined;
        this.currentFilters = undefined;
        this.currentFilterMode = undefined;
    }

    /**
     * 데이터를 로딩했는지 여부를 가져온다.
     *
     * @returns 데이터 로딩여부
     */
    isLoaded(): boolean {
        return this.getProvider().isLoaded();
    }

    /**
     * 데이터를 불러온다.
     *
     * @param is_fire_event - 이벤트 발생여부
     */
    async load(is_fire_event: boolean = true): Promise<void> {
        if (this.#loading !== null) {
            return this.#loading;
        }

        this.#loading = (async () => {
            if (this.provider.isLoadable() == true) {
                await this.provider.load();

                const isRemoteFiltered =
                    this.provider instanceof RemoteProvider && (this.provider as RemoteProvider).remoteFilter == true;
                if (isRemoteFiltered == false) {
                    await this.#filter(false);
                } else {
                    this.recordIds = [...this.rootIds];
                    this.currentFilters = this.filters == null ? null : { ...this.filters };
                    this.currentFilterMode = this.filterMode;
                }
                await this.#group(false);

                if (is_fire_event == true) {
                    await this.onLoad();
                }
            }
        })();

        try {
            await this.#loading;
        } finally {
            this.#loading = null;

            /**
             * 로딩이 진행되는 동안 release() 에 의한 제거 요청이 있었다면,
             * 로딩이 종료된 시점에 다시 제거를 시도한다.
             * referenceCount 가 0인지만으로는 "로딩 중 release() 로 참조가 모두 해제된 경우"와
             * "애초에 한 번도 retain() 된 적이 없는 경우"를 구분할 수 없으므로 별도 플래그로 판단한다.
             */
            if (this.#pendingRelease == true && this.autoDestroy == true && this.isDestroyed == false) {
                this.#pendingRelease = false;
                this.destroy();
            }
        }
    }

    /**
     * 객체를 더이상 참조하지 않는다고 선언한다.
     * 데이터를 불러오는 중에 참조가 모두 해제되는 경우, 로딩이 종료된 시점에 다시 제거를 시도한다.
     */
    release(): void {
        if (this.#loading !== null && this.referenceCount == 1) {
            this.#pendingRelease = true;
        }

        super.release();
    }

    /**
     * 특정 페이지의 데이터를 불러온다.
     *
     * @param page - 불러올페이지
     */
    async loadPage(page: number | Record | { [key: string]: any }, is_reload: boolean = false): Promise<void> {
        if (is_reload == true || this.provider.page !== page || this.provider.isLoadable() == true) {
            await this.reset();
        }

        this.provider.setPage(page);
        await this.load();
    }

    /**
     * 데이터를 다시 불러온다.
     */
    async reload(): Promise<void> {
        await this.reset();
        await this.load();
    }

    /**
     * 데이터가 로드되기 전 이벤트를 처리한다.
     */
    async onBeforeLoad(): Promise<void> {
        await this.fireEvent('beforeLoad', [this]);
    }

    /**
     * 데이터가 로드되었을 때 이벤트를 처리한다.
     */
    async onLoad(): Promise<void> {
        await this.fireEvent('load', [this]);
    }

    /**
     * 데이터가 변경되었을 때 이벤트를 처리한다.
     *
     * @param record - 변경된 레코드
     * @param is_fire_event - 이벤트 발생여부
     */
    async onEdit(record: Record, is_fire_event: boolean = true): Promise<void> {
        if (record.getUpdated() !== null) {
            this.updatedRecords.set(record.getId(), record);
        } else {
            this.updatedRecords.delete(record.getId());
        }

        if (is_fire_event == true) {
            await this.fireEvent('edit', [record]);
        }
    }

    /**
     * 객체가 제거가능한지 확인한다.
     * 데이터를 불러오는 중인 경우, 로딩이 종료되기 전까지는 제거할 수 없다.
     */
    isDestroyable(): boolean {
        return super.isDestroyable() == true && this.#loading === null;
    }

    /**
     * 객체를 제거한다.
     */
    destroy(): void {
        if (this.isDestroyable() == true) {
            this.provider.destroy();
            this.provider = null;

            for (const record of this.records.values()) {
                record.destroy();
            }
            this.records.clear();
            this.updatedRecords.clear();
            this.deletedRecords.clear();

            for (const group of this.groups.values()) {
                group.reset();
            }

            this.groups.clear();
            this.recordIds = [];
            this.groupIds = [];
        }

        super.destroy();
    }

    /**
     * 데이터를 그룹핑한다.
     *
     * @param is_fire_event - update 이벤트 발생여부
     */
    #groupCancel: (() => void) | null = null;
    async #group(is_fire_event: boolean = true): Promise<void> {
        if (this.isLoaded() == false) {
            return;
        }

        this.#groupCancel?.();

        const cancel = { value: false };
        this.#groupCancel = () => {
            cancel.value = true;
        };

        const isRemoteGroup = this.provider instanceof RemoteProvider && this.provider.remoteGroup == true;
        const isRemoteSort = this.provider instanceof RemoteProvider && this.provider.remoteSort == true;

        this.groupIds = [];
        this.groups.clear();

        this.recordIds.forEach((recordId, index) => {
            const record = this.get(recordId);
            const groupId = this.grouper?.field ? record.get(this.grouper?.field) : 'none';
            if (this.groups.has(groupId) == false) {
                this.groups.set(groupId, new Group(this as any, groupId));
                if (isRemoteGroup == true) {
                    this.groupIds.push(groupId);
                }
            }

            this.groups.get(groupId).addRecordId(record.getId());
        });

        /**
         * 원격그룹이 아닐 경우 그룹이 정렬되어 있지 않으므로, 그룹을 정렬한다.
         */
        if (isRemoteGroup == false) {
            const chunks = new Chunks<Group>([...this.groups.values()]);
            const sortedGroups = await chunks.sort((left, right) => {
                return Format.sorter(
                    this.grouper.sorters,
                    left.getRecords()[0].getData(),
                    right.getRecords()[0].getData()
                );
            });

            if (cancel.value == true) {
                return;
            }

            sortedGroups.forEach((group) => {
                this.groupIds.push(group.id);
            });
        }

        this.currentGrouper = this.grouper == null ? null : { ...this.grouper };

        if (cancel.value == true) {
            return;
        }

        if (isRemoteSort == false) {
            await this.#sort(false);
        }

        if (is_fire_event == true) {
            this.fireEvent('update', [this]);
        }

        this.#groupCancel = null;
    }

    /**
     * 데이터를 정렬한다.
     *
     * @param is_fire_event - update 이벤트 발생여부
     * @returns 정렬실행여부
     */
    #sortCancel: (() => void) | null = null;
    async #sort(is_fire_event: boolean = true): Promise<boolean> {
        if (this.isLoaded() == false || this.sorters === null) {
            return false;
        }

        this.#sortCancel?.();

        const cancel = { value: false };
        this.#sortCancel = () => {
            cancel.value = true;
        };

        const promises: Promise<void>[] = [];
        this.groups.forEach((group) => {
            promises.push(group.sort(this.sorters));
        });
        await Promise.all(promises);

        if (cancel.value == true) {
            return false;
        }

        this.recordIds = [];
        this.groupIds.forEach((groupId) => {
            const records = this.groups.get(groupId).getRecordIds();
            this.recordIds = [...this.recordIds, ...records];
        });
        this.currentSorters = { ...this.sorters };

        if (is_fire_event == true) {
            this.fireEvent('update', [this]);
        }

        this.#sortCancel = null;

        return true;
    }

    /**
     * 데이터를 필터링한다.
     *
     * @param is_fire_event - update 이벤트 발생여부
     * @returns 필터실행여부
     */
    #filterCancel: (() => void) | null = null;
    async #filter(is_fire_event: boolean = true): Promise<boolean> {
        if (this.isLoaded() == false) {
            return false;
        }

        this.#filterCancel?.();

        const cancel: { value: boolean } = { value: false };
        this.#filterCancel = () => {
            cancel.value = true;
        };

        const recordIds: string[] = [];
        const chunks = new Chunks<Record>(this.getRecords(true));
        await chunks.execute(async (record) => {
            if ((await record.isFiltered(this.filters, this.filterMode)) == true) {
                recordIds.push(record.getId());
            }
        });

        if (cancel.value == true) {
            return false;
        }

        this.recordIds = recordIds;
        this.currentFilters = this.filters == null ? null : { ...this.filters };
        this.currentFilterMode = this.filterMode;

        await this.#group(false);

        if (is_fire_event == true) {
            this.fireEvent('update', [this]);
        }

        this.#filterCancel = null;

        return true;
    }
}
